import type { Lead } from './types';

/**
 * Which leads are the same person.
 *
 * ── Why this stopped being a name match ────────────────────────────────────
 *
 * It matched on `first_name + last_name`, lowercased. That works for the
 * duplicate somebody types twice and fails for every duplicate that arrives by
 * import — which is nearly all of them.
 *
 * Measured 1 Sep. Two real pairs sat unnoticed for weeks:
 *
 *   Mike / Keech          + mike@liquid-iv.com        (29 Jul, 2 touches)
 *   "Mike Keech," / mike@liquid-iv.com                (12 Aug, 2 touches)
 *
 *   Bret / Eby            + bret@healthyorigins.com   (29 Jul, 1 touch)
 *   "Bret Eby\tbret@healthyorigins.com"               (14 Aug, 0 touches)
 *
 * Same person, same email, invisible to a name match — because the second copy
 * of each pair had the email pasted INTO the name field, tab and all. The
 * mangled name is what hid the duplicate, so the population most likely to be
 * duplicated was exactly the population the detector could not see.
 *
 * ── What it matches on now ─────────────────────────────────────────────────
 *
 * Every email a lead carries, wherever it is written — the email column, and
 * any address sitting in a name field. Plus the name, as before.
 *
 * Reading emails out of the NAME is the part that earns its keep. Without it
 * the two pairs above only become findable after somebody has cleaned the
 * names by hand, which is the wrong way round: the detector should be what
 * tells you the cleaning is needed.
 *
 * ── Why groups are merged rather than listed per key ───────────────────────
 *
 * A lead can match one lead by name and a different one by email. Listing each
 * key separately would show the same person in two "duplicate groups" and
 * invite two merges, the second of which fails because the first deleted a
 * row. Anything sharing any key ends up in one group.
 */

/** An email, lowercased and trimmed, or null when the text holds none. */
function emailIn(text: string): string | null {
  const match = /[^\s,;<>()"']+@[^\s,;<>()"']+\.[^\s,;<>()"']+/.exec(text);
  return match === null ? null : match[0].toLowerCase().replace(/[.,;]+$/, '');
}

/**
 * Every key that identifies this lead: its name, and every email it carries.
 *
 * A lead with no name and no email produces nothing and is never grouped —
 * matching two blank records to each other would report a duplicate for every
 * pair of empty rows.
 */
export function leadKeys(lead: Lead): string[] {
  const keys = new Set<string>();

  const first = (lead.first_name ?? '').trim();
  const last = (lead.last_name ?? '').trim();

  // The name, with any email stripped out of it first. "Bret Eby
  // bret@healthyorigins.com" has to key as "bret eby" or it will never match
  // the clean "Bret" + "Eby" record.
  const nameText = `${first} ${last}`
    .replace(/[^\s,;<>()"']+@[^\s,;<>()"']+\.[^\s,;<>()"']+/g, ' ')
    .replace(/[,;\t]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
  if (nameText !== '') {
    keys.add(`name:${nameText}`);
  }

  // Every email, wherever it is written.
  for (const text of [lead.email ?? '', first, last]) {
    const found = emailIn(text);
    if (found !== null) {
      keys.add(`email:${found}`);
    }
  }

  return [...keys];
}

/**
 * Groups of leads that look like the same person. Singletons are dropped.
 *
 * Union–find over the keys: two leads land together when they share ANY key,
 * and a chain of shared keys pulls the whole chain into one group.
 */
export function duplicateLeadGroups(leads: Lead[]): Lead[][] {
  const parent = new Map<string, string>();
  const find = (id: string): string => {
    let root = id;
    while (parent.get(root) !== undefined && parent.get(root) !== root) {
      root = parent.get(root) ?? root;
    }
    return root;
  };
  const union = (a: string, b: string) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) {
      parent.set(rootA, rootB);
    }
  };

  const owner = new Map<string, string>();
  for (const lead of leads) {
    parent.set(lead.id, parent.get(lead.id) ?? lead.id);
    for (const key of leadKeys(lead)) {
      const held = owner.get(key);
      if (held === undefined) {
        owner.set(key, lead.id);
      } else {
        union(lead.id, held);
      }
    }
  }

  const grouped = new Map<string, Lead[]>();
  for (const lead of leads) {
    // A lead with no keys at all — no name, no email anywhere — is not a
    // duplicate of anything. Grouping blanks together would report every pair
    // of empty rows as the same person.
    if (leadKeys(lead).length === 0) {
      continue;
    }
    const root = find(lead.id);
    grouped.set(root, [...(grouped.get(root) ?? []), lead]);
  }

  return [...grouped.values()].filter((group) => group.length > 1);
}
